/**
 * URL Validation Utilities
 *
 * Security utilities for validating URLs and redirect paths
 * to prevent open redirect vulnerabilities.
 */

/**
 * Validates that a redirect path is safe (internal to the application).
 *
 * Prevents open redirect attacks by ensuring:
 * - Path is relative (starts with /)
 * - Path doesn't contain protocol (://)
 * - Path doesn't use protocol-relative URLs (//)
 * - Path doesn't contain encoded characters that could bypass checks
 *
 * @param path - The redirect path to validate
 * @param fallback - Fallback path if validation fails (default: '/dashboard')
 * @returns The original path if safe, otherwise the fallback
 */
export function getSafeRedirectPath(path: string | null, fallback = '/dashboard'): string {
  if (!path) {
    return fallback;
  }

  // Decode the path to prevent encoded bypass attempts
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(path);
  } catch {
    // If decoding fails, it's likely malicious
    return fallback;
  }

  // Must start with a single forward slash (not //)
  if (!decodedPath.startsWith('/') || decodedPath.startsWith('//')) {
    return fallback;
  }

  // Must not contain protocol indicators
  if (decodedPath.includes('://')) {
    return fallback;
  }

  // Must not contain backslashes (could be used for path traversal on Windows)
  if (decodedPath.includes('\\')) {
    return fallback;
  }

  // Must not contain null bytes or other dangerous characters
  if (decodedPath.includes('\0') || decodedPath.includes('\n') || decodedPath.includes('\r')) {
    return fallback;
  }

  // Must not start with /\ (mixed separator that could cause issues)
  if (/^\/\\/.test(decodedPath)) {
    return fallback;
  }

  return path;
}

/**
 * Validates that a URL is from an allowed domain.
 *
 * Used for validating external URLs before opening them (e.g., Stripe URLs).
 *
 * @param url - The URL to validate
 * @param allowedDomains - List of allowed domains (e.g., ['stripe.com', 'checkout.stripe.com'])
 * @returns True if the URL is from an allowed domain
 */
export function isAllowedExternalUrl(url: string, allowedDomains: string[]): boolean {
  if (!url) {
    return false;
  }

  try {
    const parsedUrl = new URL(url);

    // Only allow https
    if (parsedUrl.protocol !== 'https:') {
      return false;
    }

    // Check if domain matches any allowed domain (including subdomains)
    return allowedDomains.some((domain) => {
      return parsedUrl.hostname === domain || parsedUrl.hostname.endsWith(`.${domain}`);
    });
  } catch {
    // Invalid URL
    return false;
  }
}

/**
 * Validates that a URL is safe to use as an image source.
 *
 * Prevents javascript: and data: URLs that could execute code.
 *
 * @param url - The URL to validate
 * @returns True if the URL is safe for image src
 */
export function isSafeImageUrl(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }

  try {
    const parsedUrl = new URL(url);

    // Only allow http and https protocols
    return parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:';
  } catch {
    // Relative URLs are allowed
    if (url.startsWith('/') && !url.startsWith('//')) {
      return true;
    }
    return false;
  }
}
